import { AI_COLOR_FALLBACK, PRIMARY_FALLBACK, colors, shadows, field } from "Shared/tokens"
import { useEffect } from "react"
import { useBusinessColors, useBusinessFormStyle } from "Shared/hooks/useBusinessColors"
import { computeAccentColor, computeMenuColors } from "Utils/colors"
import { hexToHsl, hslToHex } from "Shared/components/JsonForms/Layout/color-utils"
import { loadCachedBusinessColors } from "Shared/utils/businessColorsCache"

const setVar = (root: CSSStyleDeclaration, name: string, value?: string | null) => {
  if (value) root.setProperty(name, value)
  else root.removeProperty(name)
}

// Static tokens → CSS vars (injected once, never change for the app lifetime)
const STATIC_VARS: Record<string, string> = {
  // Gray palette
  "--gray-50": colors.gray[50], "--gray-100": colors.gray[100],
  "--gray-200": colors.gray[200], "--gray-300": colors.gray[300],
  "--gray-400": colors.gray[400], "--gray-500": colors.gray[500],
  "--gray-600": colors.gray[600], "--gray-700": colors.gray[700],
  "--gray-900": colors.gray[900],
  // Slate palette
  "--slate-50": colors.slate[50], "--slate-100": colors.slate[100],
  "--slate-200": colors.slate[200], "--slate-300": colors.slate[300],
  "--slate-400": colors.slate[400], "--slate-500": colors.slate[500],
  "--slate-600": colors.slate[600], "--slate-700": colors.slate[700],
  "--slate-900": colors.slate[900],
  // Semantic colors
  "--black": colors.black, "--white": colors.white,
  "--background": colors.background, "--border": colors.border,
  "--border-input": colors.borderInput,
  "--text-primary": colors.textPrimary,
  "--text-secondary": colors.textSecondary,
  "--text-muted": colors.textMuted,
  // Status colors
  "--status-success-bg": colors.status.success.bg,
  "--status-success-text": colors.status.success.text,
  "--status-success-icon": colors.status.success.icon,
  "--status-warning-bg": colors.status.warning.bg,
  "--status-warning-text": colors.status.warning.text,
  "--status-warning-icon": colors.status.warning.icon,
  "--status-error-bg": colors.status.error.bg,
  "--status-error-text": colors.status.error.text,
  "--status-error-icon": colors.status.error.icon,
  "--status-info-bg": colors.status.info.bg,
  "--status-info-text": colors.status.info.text,
  "--status-info-icon": colors.status.info.icon,
  // Shadows
  "--shadow-sm": shadows.sm,
  "--shadow-md": shadows.md,
  "--shadow-dropdown": shadows.dropdown,
  "--shadow-lg": shadows.lg,
  "--shadow-overlay": shadows.overlay,
  "--shadow-top": shadows.top,
  "--shadow-focus": shadows.focus,
  // Form fields (defaults — tenant "style" knobs may override --field-radius /
  // --field-min-height / --field-pad-y at runtime in the dynamic effect below)
  "--field-bg": field.bg,
  // Valeur d'amorçage seulement : le bloc dynamique la recalcule à partir de --field-bg.
  "--field-bg-disabled": field.bgDisabled,
  "--field-border": field.border,
  "--field-border-hover": field.borderHover,
  "--field-text": field.text,
  "--field-placeholder": field.placeholder,
  "--field-label-color": field.labelColor,
  "--field-pad-x": `${field.paddingX}px`,
  // NB: --field-radius / --field-min-height / --field-pad-y are set in the
  // DYNAMIC effect below because the tenant "Style" knobs may override them.
}

/**
 * Surface d'un champ **verrouillé** (non éditable), dérivée du fond des champs du tenant.
 *
 * Pourquoi dériver plutôt que poser un gris fixe : un champ verrouillé garde le fond
 * `--field-bg` en consultation et prenait `#f3f4f6` en saisie — soit un changement de
 * *couleur*, pas d'*état*, qui se lit comme une incohérence dès que le tenant choisit
 * autre chose que du blanc. En dérivant, l'écart reste une nuance du même fond.
 *
 * Transformation : même teinte, saturation atténuée (le « grisé » est d'abord une perte
 * de saturation) et luminosité déplacée **relativement** — donc sans clamp possible sur
 * un fond déjà très clair ou très sombre. Sur le blanc par défaut le résultat est
 * indiscernable du `#f3f4f6` historique.
 */
export function deriveDisabledFieldBg(fieldBg: string): string {
  const { h, s, l } = hexToHsl(fieldBg)
  const mutedS = s * 0.6
  // Un fond clair s'assombrit, un fond sombre s'éclaircit : dans les deux cas la
  // surface verrouillée recule d'un cran par rapport à la surface saisissable.
  const shiftedL = l > 50 ? l * 0.95 : l + (100 - l) * 0.08
  return hslToHex(h, mutedS, shiftedL)
}

// Tenant "Style" knob → CSS value mappings for form controls.
const FIELD_RADIUS: Record<string, string> = {
  sharp: "4px",
  default: `${field.radius}px`,
  rounded: "12px",
}
const FIELD_DENSITY: Record<string, { minHeight: string; padY: string }> = {
  comfortable: { minHeight: `${field.minHeight}px`, padY: `${field.paddingY}px` },
  compact: { minHeight: "38px", padY: "7px" },
}

/**
 * Cartes de section du formulaire (« Group » : IDENTIFICATION, QUALIFICATION…).
 * Surface distincte de celle des champs : une carte porte des champs, elle n'en est
 * pas un — d'où des réglages séparés plutôt qu'un partage de `--field-*`.
 * Rayons plus larges que les champs (une carte est un contenant).
 */
export const GROUP_RADIUS: Record<string, string> = {
  sharp: "4px",
  default: "12px",
  rounded: "20px",
}
export const GROUP_SHADOW: Record<string, string> = {
  none: "none",
  soft: shadows.sm,
  strong: shadows.lg,
}
/** Valeurs par défaut = le rendu figé en dur avant que le réglage n'existe. */
export const GROUP_DEFAULT_RADIUS = GROUP_RADIUS.default
export const GROUP_DEFAULT_SHADOW = "0 1px 3px rgba(0, 0, 0, 0.04)"

// Optional tenant style: when `formStyle.readBoxed` is on, read-only field
// displays are shown with the same box as edit mode. Injected once; gated by the
// `:root[data-field-read-boxed]` attribute set in the dynamic effect. Targets the
// shared read-only display classes (.value_display ×21, .ro_value ×6).
const READ_BOXED_STYLE_ID = "ds-field-read-boxed"
const READ_BOXED_CSS = `
:root[data-field-read-boxed="1"] .value_display,
:root[data-field-read-boxed="1"] .ro_value {
  border: 1px solid var(--field-border, #d7dee8);
  background: var(--field-bg, #fff);
  border-radius: var(--field-radius, 8px);
  min-height: var(--field-min-height, 44px);
  padding: var(--field-pad-y, 11px) var(--field-pad-x, 12px);
  box-sizing: border-box;
}`
function ensureReadBoxedStyle(): void {
  if (typeof document === "undefined" || document.getElementById(READ_BOXED_STYLE_ID)) return
  const el = document.createElement("style")
  el.id = READ_BOXED_STYLE_ID
  el.textContent = READ_BOXED_CSS
  document.head.appendChild(el)
}

// Module-level flag is intentional (app-lifetime singleton). Tests that reset
// :root must re-import the module to reset this flag.
let staticInjected = false

// At module-load (before React mounts), apply last-known business colors from
// localStorage to :root so the very first render sees correct branding instead
// of fallback purple. Apollo will overwrite when fresh data arrives.
if (typeof document !== "undefined") {
  const cached = loadCachedBusinessColors()
  if (cached) {
    const root = document.documentElement.style
    const p = cached.primaryColor || PRIMARY_FALLBACK
    const accent = computeAccentColor(p)
    root.setProperty("--color-primary", p)
    root.setProperty("--color-primary-accent", accent)
    if (cached.aiColor) root.setProperty("--color-ai", cached.aiColor)
    if (cached.sideMenuColor) root.setProperty("--color-sidebar", cached.sideMenuColor)
    if (cached.alternativeColor) root.setProperty("--color-alternative", cached.alternativeColor)
    if (cached.illustrationColor) root.setProperty("--color-illustration", cached.illustrationColor)
    if (cached.bannerColor) root.setProperty("--color-banner", cached.bannerColor)
    // Banner falls back to white (CSS var left unset) when neither headerColor nor
    // bannerColor is defined — NOT to the primary brand color. Setting it to `p`
    // would paint a fresh tenant's banner with PRIMARY_FALLBACK (#3b82f6) instead
    // of white, since the consumer uses `var(--color-header, white)`.
    const hdr = cached.headerColor || cached.bannerColor
    if (hdr) root.setProperty("--color-header", hdr)
    const menu = computeMenuColors(accent, p)
    if (menu?.activeText) root.setProperty("--color-menu-text", menu.activeText)
    if (menu?.activeBg) root.setProperty("--color-menu-active", menu.activeBg)
    if (menu?.hoverBg) root.setProperty("--color-menu-hover", menu.hoverBg)
  }
}

/**
 * Injects design tokens (static) and business colors (dynamic) as CSS variables
 * on `document.documentElement`. Called once via `<BusinessThemeProvider>` at
 * app root.
 *
 * No cleanup: CSS vars on `:root` are app-lifetime global state. (Diverges
 * intentionally from rd/ia_ui, where the cleanup wiped static vars on every
 * dynamic re-render and never restored them.)
 */
export function useBusinessTheme(): void {
  const {
    primaryColor: primary,
    aiColor,
    sideMenuColor: sideMenu,
    alternativeColor: alternative,
    illustrationColor: illustration,
    bannerColor: banner,
    headerColor,
  } = useBusinessColors()

  const {
    radius: fieldRadius,
    density: fieldDensity,
    fieldBg,
    readBoxed,
    textMaxHeight,
    tableRows,
    groupBg,
    groupRadius,
    groupShadow,
  } = useBusinessFormStyle()

  // Static tokens: inject once on first mount
  useEffect(() => {
    if (staticInjected) return
    const root = document.documentElement.style
    for (const [name, value] of Object.entries(STATIC_VARS)) {
      root.setProperty(name, value)
    }
    ensureReadBoxedStyle()
    staticInjected = true
  }, [])

  // Dynamic tokens: re-inject when business colors change
  useEffect(() => {
    const root = document.documentElement.style
    const p = primary || PRIMARY_FALLBACK
    const accent = computeAccentColor(p)

    root.setProperty("--color-primary", p)
    root.setProperty("--color-primary-accent", accent)
    root.setProperty("--color-ai", aiColor || AI_COLOR_FALLBACK)
    setVar(root, "--color-sidebar", sideMenu)
    setVar(root, "--color-alternative", alternative)
    setVar(root, "--color-illustration", illustration)
    setVar(root, "--color-banner", banner)
    // headerColor is the granular per-tenant override; banner is the broader brand
    // color used for banners. Most-specific wins, then banner. When neither is set
    // the var is removed so the consumer's `var(--color-header, white)` fallback
    // applies — the banner must default to white, not the primary brand color.
    setVar(root, "--color-header", headerColor || banner)

    const menu = computeMenuColors(accent, p)
    setVar(root, "--color-menu-text", menu?.activeText)
    setVar(root, "--color-menu-active", menu?.activeBg)
    setVar(root, "--color-menu-hover", menu?.hoverBg)

    // Form-field style knobs (fall back to DS defaults when the tenant hasn't set them)
    root.setProperty("--field-radius", FIELD_RADIUS[fieldRadius || ""] || `${field.radius}px`)
    const density = FIELD_DENSITY[fieldDensity || ""] || FIELD_DENSITY.comfortable
    root.setProperty("--field-min-height", density.minHeight)
    root.setProperty("--field-pad-y", density.padY)
    // Field background: tenant override or DS default (white)
    const surface = fieldBg || field.bg
    root.setProperty("--field-bg", surface)
    // Surface des champs verrouillés : dérivée du fond ci-dessus, jamais un gris fixe
    // (sinon un champ verrouillé change de COULEUR entre lecture et saisie).
    root.setProperty("--field-bg-disabled", deriveDisabledFieldBg(surface))
    // Read-only fields boxed like edit mode (gates the injected READ_BOXED_CSS)
    if (readBoxed) document.documentElement.setAttribute("data-field-read-boxed", "1")
    else document.documentElement.removeAttribute("data-field-read-boxed")
    // Max height (px) of text areas / rich editors: the chosen value is a CAP — the field grows
    // with its content up to this height then scrolls (write and read alike). DS default otherwise.
    root.setProperty("--field-content-max-height", textMaxHeight ? `${textMaxHeight}px` : "106px")
    // Max height of BackLink relation tables: tableRows × row(~42px) + header(~50px), else DS default
    root.setProperty(
      "--field-table-max-height",
      tableRows ? `${tableRows * 42 + 50}px` : "300px"
    )

    // Cartes de section du formulaire
    root.setProperty("--group-bg", groupBg || colors.white)
    root.setProperty("--group-radius", GROUP_RADIUS[groupRadius || ""] || GROUP_DEFAULT_RADIUS)
    // `none` est une valeur choisie : ne pas la confondre avec « non réglé ».
    root.setProperty(
      "--group-shadow",
      groupShadow && groupShadow in GROUP_SHADOW ? GROUP_SHADOW[groupShadow] : GROUP_DEFAULT_SHADOW
    )
  }, [
    primary,
    aiColor,
    sideMenu,
    alternative,
    illustration,
    banner,
    headerColor,
    fieldRadius,
    fieldDensity,
    fieldBg,
    readBoxed,
    textMaxHeight,
    tableRows,
    groupBg,
    groupRadius,
    groupShadow,
  ])
}

export default useBusinessTheme
